import { useState, useRef, createContext, useContext, forwardRef, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { ClientConfigurationError } from '@/lib/errors/client';

import { fontStyles } from '@/styles/typography';

import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';

import { classNames } from '@/utils/classnames';

import animations from './animations.module.css';

type AccordionType = 'single' | 'multiple';
export type AccordionValue = string | string[];

interface AccordionContextValue {
  /** Set containing the currently expanded accordion values */
  expandedValues: Set<AccordionValue>;
  /** The accordion's type of expansion ('single' or 'multiple') */
  type: AccordionType;
  /** Callback function triggered when an accordion item's expanded state changes */
  onValueChange: (value: AccordionValue) => void;
  onKeyDownHandler: (value: AccordionValue) => (e: React.KeyboardEvent<HTMLButtonElement>) => void;
}

const AccordionContext = createContext<AccordionContextValue>({
  expandedValues: new Set(),
  type: 'single',
  onValueChange: (_value: AccordionValue) => {},
  onKeyDownHandler: (_value: AccordionValue) => (_e: React.KeyboardEvent<HTMLButtonElement>) => {},
});

interface AccordionRootProps extends React.ComponentPropsWithoutRef<'div'> {
  /**
   * Use `single` for an accordion that allows only one item to be expanded at
   * a time, and `multiple` for an accordion that allows multiple items to be expanded at a time.
   */
  type: AccordionType;
  /**
   * The value of the items whose contents are expanded when the accordion is initially rendered. Use
   * `defaultValue` if you do not need to control the state of an accordion.
   */
  defaultValue?: AccordionValue;
  /**
   * The callback that fires when the state of the accordion changes.
   */
  onValueChange?: (value: AccordionValue) => void;
  /**
   * When type is "single", allows closing content when clicking trigger for an open item.
   */
  collapsible?: boolean;
}
const Root = forwardRef<HTMLDivElement, AccordionRootProps>(function Root(
  {
    className,
    children,
    type = 'single',
    defaultValue,
    onValueChange,
    collapsible = false,
    ...props
  },
  ref,
) {
  const [expandedValues, setExpandedValues] = useState<Set<AccordionValue>>(() => {
    const initial = defaultValue ? [defaultValue] : [];
    return new Set(initial);
  });

  const handleValueChange = (clickedValue: AccordionValue) => {
    const newValues = new Set(expandedValues);

    if (newValues.has(clickedValue)) {
      if (type === 'single' && newValues.size === 1 && !collapsible) {
        return;
      }
      newValues.delete(clickedValue);
    } else {
      if (type === 'single') {
        newValues.clear();
      }
      newValues.add(clickedValue);
    }

    setExpandedValues(newValues);

    if (onValueChange) {
      if (type === 'single') {
        onValueChange(clickedValue);
      } else {
        onValueChange(Array.from(newValues) as string[]);
      }
    }
  };

  const onKeyDownHandler =
    (value: AccordionValue) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const buttons = document.querySelectorAll<HTMLButtonElement>('[data-accordion-trigger]');
      const buttonArray = Array.from(buttons);
      const currentIndex = buttonArray.indexOf(e.currentTarget as HTMLButtonElement);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (currentIndex < buttonArray.length - 1) {
            buttonArray[currentIndex + 1].focus();
          } else {
            buttonArray[0].focus();
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (currentIndex > 0) {
            buttonArray[currentIndex - 1].focus();
          } else {
            buttonArray[buttonArray.length - 1].focus();
          }
          break;
        case 'Home':
          e.preventDefault();
          buttonArray[0].focus();
          break;
        case 'End':
          e.preventDefault();
          buttonArray[buttonArray.length - 1].focus();
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          handleValueChange(value);
          break;
      }
    };

  return (
    <AccordionContext.Provider
      value={{
        expandedValues,
        type,
        onValueChange: handleValueChange,
        onKeyDownHandler,
      }}
    >
      <div
        {...props}
        ref={ref}
        className={twMerge(classNames('flex', 'flex-col', 'gap-4', className))}
        data-type={type}
      >
        {children}
      </div>
    </AccordionContext.Provider>
  );
});

interface AccordionItemContextValue {
  /** The unique identifier value for this accordion item */
  value: AccordionValue;
  /** The current expanded state of this specific item */
  isExpanded: boolean;
}
const AccordionItemContext = createContext<AccordionItemContextValue | null>(null);
interface AccordionItemProps extends React.ComponentPropsWithoutRef<'div'> {
  /** The unique identifier value for this accordion item */
  value: AccordionValue;
}

const Item = forwardRef<HTMLDivElement, AccordionItemProps>(function Item(
  { value, className, children, ...props },
  ref,
) {
  const accordionContext = useContext(AccordionContext);
  if (!accordionContext) {
    throw new ClientConfigurationError('AccordionItem must be used within an Accordion Root');
  }
  const isExpanded = accordionContext.expandedValues.has(value);

  return (
    <AccordionItemContext.Provider value={{ value, isExpanded }}>
      <div
        {...props}
        ref={ref}
        className={twMerge(
          classNames(
            'rounded-md',
            'transition-[background-color_0.3s_ease-in-out]',
            'hover:bg-token-alphalight-200',
            'data-[state=open]:bg-token-alphalight-200',
            className,
          ),
        )}
        data-state={isExpanded ? 'open' : 'closed'}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
});

interface AccordionTriggerProps extends React.ComponentPropsWithoutRef<'button'> {
  /** Determines whether to show the chevron icon or not */
  showChevron?: boolean;
}

const Trigger = forwardRef<HTMLButtonElement, AccordionTriggerProps>(function Trigger(
  { className, children, showChevron = true, ...props },
  ref,
) {
  const { onValueChange, onKeyDownHandler } = useContext(AccordionContext);
  const itemContext = useContext(AccordionItemContext);

  if (!itemContext) {
    throw new ClientConfigurationError('AccordionTrigger must be used within an AccordionItem');
  }

  return (
    <h3
      className="flex m-0 cursor-pointer"
      id={`accordion-header-${itemContext.value}`}
      data-state={itemContext.isExpanded ? 'open' : 'closed'}
    >
      <button
        {...props}
        ref={ref}
        type="button"
        className={twMerge(
          classNames(
            fontStyles({
              variant: 'LABEL_LG',
              class: [
                'flex',
                'items-center',
                'justify-between',
                'p-4',
                'flex-1',
                'before:[display:none]',
                'after:[display:none]',
                'data-[state=open]:pb-2',
              ],
            }),
            className,
          ),
        )}
        onClick={() => onValueChange(itemContext.value)}
        onKeyDown={onKeyDownHandler(itemContext.value)}
        aria-expanded={itemContext.isExpanded}
        data-state={itemContext.isExpanded ? 'open' : 'closed'}
        aria-controls={`accordion-panel-${itemContext.value}`}
        data-accordion-trigger
        id={`accordion-trigger-${itemContext.value}`}
      >
        {children}
        {showChevron && (
          <ChevronDownIcon
            className={classNames(
              'text-token-gray-700',
              'origin-[center_center]',
              itemContext.isExpanded
                ? animations['animate-chevron-open']
                : animations['animate-chevron-closed'],
            )}
            height={16}
            width={16}
          />
        )}
      </button>
    </h3>
  );
});

const Content = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(function Content(
  { className, children, ...props },
  ref,
) {
  const itemContext = useContext(AccordionItemContext);
  /**
   * The internal ref is used to measure and set the content's height for animations.
   * This enables smooth expand/collapse transitions by updating a custom CSS property
   * */
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef || typeof contentRef === 'function' || !contentRef.current) return;
    contentRef.current.style.setProperty(
      '--accordion-content-height',
      `${contentRef.current.scrollHeight}px`,
    );
  }, [itemContext, contentRef]);

  if (!itemContext) {
    throw new ClientConfigurationError('AccordionContent must be used within an AccordionItem');
  }
  const mergedRef = useMergeRefs(contentRef, ref);

  return (
    <div
      {...props}
      ref={mergedRef}
      className={twMerge(
        classNames(
          fontStyles({
            variant: 'BODY_MD',
            class: [
              'text-token-gray-800',
              'px-4',
              'pb-4',
              'overflow-hidden',
              'opacity-100',
              'before:hidden',
              'after:hidden',
              'data-[state=closed]:-mt-4',
              'data-[state=closed]:h-0',
            ],
          }),
          itemContext.isExpanded
            ? animations['animate-accordion-slide-down']
            : animations['animate-accordion-slide-up'],
          className,
        ),
      )}
      data-state={itemContext.isExpanded ? 'open' : 'closed'}
    >
      <div
        className={classNames(
          itemContext.isExpanded
            ? animations['animate-content-fade-in']
            : animations['animate-content-fade-out'],
        )}
      >
        {children}
      </div>
    </div>
  );
});

const useMergeRefs = <T extends object>(
  ...refs: Array<React.MutableRefObject<T> | React.Ref<T> | null>
) => {
  return (value: T): void => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref !== null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
};

/**
 * Accordion component that manages collapsible sections of content. The Accordion component
 * is composed of a Root, Item, Trigger, and Content components.
 *
 * Single and multiple expansion types are supported.
 *
 * @example
 * Single expansion type:
 * ```tsx
 * <Accordion.Root type="single" defaultValue="item-1">
 *   <Accordion.Item value="item-1">
 *     <Accordion.Trigger>Section 1</Accordion.Trigger>
 *     <Accordion.Content>Content 1</Accordion.Content>
 *   </Accordion.Item>
 * </Accordion.Root>
 * ```
 * Multiple expansion type:
 * ```tsx
 * <Accordion.Root type="multiple">
 *  <Accordion.Item value="item-1">
 *   <Accordion.Trigger>Section 1</Accordion.Trigger>
 *  <Accordion.Content>Content 1</Accordion.Content>
 * </Accordion.Item>
 * <Accordion.Item value="item-2">
 *  <Accordion.Trigger>Section 2</Accordion.Trigger>
 * <Accordion.Content>Content 2</Accordion.Content>
 * </Accordion.Item>
 * </Accordion.Root>
 * ```
 */
export const Accordion = {
  Root,
  Item,
  Trigger,
  Content,
};
